/**
 * Yardım Videoları — firma kullanıcısı görünümü.
 *
 * Ayarlar ve Pano sayfalarındaki "Bilgi ve Destek için" kartlarının ikisi de
 * AYNI bileşeni kullanır (`HelpVideosButton`) → iki yerdeki liste ve oynatıcı
 * birebir aynı kalır.
 *
 * Liste yalnızca Konu + Açıklama gösterir. Videoya tıklanınca:
 *   • YouTube → iframe embed (liste dışı/unlisted videolar da böyle oynar)
 *   • Sunucu  → HTML5 <video>
 *
 * PASİF VİDEOLAR: filtreleme backend'de yapılır (`GET /help-videos` yalnızca
 * is_active olanları döner) → burada ek bir kontrol gerekmez.
 *
 * HAM LİNK GÖSTERİLMEZ: backend youtube_video_id döner, URL'i burada üretiriz.
 */
import { useEffect, useState } from "react";
import { api, notifyError, API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { PlayCircle, Loader2, ArrowLeft, Youtube, Video } from "lucide-react";

function youtubeEmbedUrl(videoId) {
  // Sade www.youtube.com/embed: "Liste dışı (unlisted)" videolarla en uyumlu
  // biçim. "Özel (private)" videolar embed edilemez — bu bilinçli bir sınırdır.
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?rel=0&modestbranding=1`;
}

function mediaUrl(mediaId) {
  return `${API_BASE}/media/${mediaId}`;
}

function VideoPlayer({ video }) {
  if (video.source === "youtube" && video.youtube_video_id) {
    return (
      <div className="relative w-full overflow-hidden rounded-sm bg-black" style={{ aspectRatio: "16 / 9" }}>
        <iframe
          key={video.id}
          src={youtubeEmbedUrl(video.youtube_video_id)}
          title={video.topic}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="help-video-youtube"
        />
      </div>
    );
  }
  if (video.source === "server" && video.media_id) {
    return (
      // Altyazı (<track>) eklenmiyor: yardım videoları master panelden
      // yükleniyor ve altyazı dosyası yönetimi bu kapsamda yok.
      <video
        key={video.id}
        src={mediaUrl(video.media_id)}
        controls
        playsInline
        preload="metadata"
        className="w-full rounded-sm bg-black"
        data-testid="help-video-server"
      />
    );
  }
  return (
    <div className="rounded-sm border-l-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      Bu video şu anda oynatılamıyor.
    </div>
  );
}

export function HelpVideosDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/help-videos");
        if (!cancelled) setVideos(data || []);
      } catch (e) {
        if (!cancelled) notifyError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="help-videos-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {selected ? selected.topic : "Yardım Videoları"}
          </DialogTitle>
          {!selected && (
            <DialogDescription>
              Sistemi kullanırken izleyebileceğiniz kısa anlatım videoları.
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </div>
        ) : selected ? (
          <div className="space-y-3">
            <VideoPlayer video={selected} />
            {selected.description && (
              <p className="text-sm text-muted-foreground">{selected.description}</p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setSelected(null)}
              className="rounded-sm"
              data-testid="help-video-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Listeye Dön
            </Button>
          </div>
        ) : videos.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground" data-testid="help-videos-empty">
            Henüz yardım videosu eklenmemiş.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="help-videos-list">
            {videos.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => setSelected(v)}
                  className="w-full text-left flex items-start gap-3 py-3 px-1 hover:bg-muted/50 transition-colors rounded-sm"
                  data-testid={`help-video-item-${v.id}`}
                >
                  <div className="h-9 w-9 shrink-0 rounded-sm bg-foreground text-amber-500 flex items-center justify-center">
                    {v.source === "youtube" ? (
                      <Youtube className="h-4 w-4" />
                    ) : (
                      <Video className="h-4 w-4" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{v.topic}</div>
                    {v.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">{v.description}</div>
                    )}
                  </div>
                  <PlayCircle className="h-5 w-5 shrink-0 text-muted-foreground mt-1" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** "Bilgi ve Destek için" kartlarına eklenen tetikleyici + dialog. */
export default function HelpVideosButton({ className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          "inline-flex items-center gap-2 font-semibold hover:text-amber-600 transition-colors"
        }
        data-testid="help-videos-btn"
      >
        <PlayCircle className="h-4 w-4 text-amber-600" />
        Yardım Videoları
      </button>
      <HelpVideosDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
